import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import VolunteerTeam, {
  VolunteerTeamBundle,
  VolunteerTeamMemeber,
  VolunteerTeamRole,
  VolunteerTeamRoleAttributes,
  VolunteerTeamsFetchResult
} from '@data/interfaces/Volunteering/volunteerteam.interface';
import { PersonAttributes } from '@interfaces/person.interface';
import { TrainingVolunteerAssignment } from '@interfaces/training.interface';

/// A member's roles after a change, and the training handed out for roles they
/// newly gained, which may be none.
export type MemberRolesResponse = {
  result: VolunteerTeamMemeber;
  autoAssignedTraining?: TrainingVolunteerAssignment[];
};

/// A role after an update, and the training its newly added modules handed to
/// people already holding it, which may be none.
export type RoleUpdateResponse = {
  result: VolunteerTeamRole;
  autoAssignedTraining?: TrainingVolunteerAssignment[];
};

export default class VolunteeringController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  static async fetchTeamsWithMembers(
    churchID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<VolunteerTeamsFetchResult>> {
    return await NetworkController.performRequestAt<VolunteerTeamsFetchResult>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/volunteering/teams`
      ),
      auth.consumeRefreshToken
    );
  }

  // One team with its members and roles.
  static async fetchTeam(
    churchID: string,
    teamID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: VolunteerTeamBundle }>> {
    return await NetworkController.performRequestAt<{ result: VolunteerTeamBundle }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.GET,
        `/v1/church/${churchID}/volunteering/teams/${teamID}`
      ),
      auth.consumeRefreshToken
    );
  }

  // Creates an empty team. The API rejects an empty name.
  static async createTeam(
    churchID: string,
    name: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: VolunteerTeam }>> {
    return await NetworkController.performRequestAt<{ result: VolunteerTeam }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/volunteering/teams`,
        { attributes: { name } }
      ),
      auth.consumeRefreshToken
    );
  }

  // Replaces the team's attributes, which today are only its name.
  static async updateTeam(
    churchID: string,
    teamID: string,
    name: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: VolunteerTeam }>> {
    return await NetworkController.performRequestAt<{ result: VolunteerTeam }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/volunteering/teams/${teamID}`,
        { attributes: { name } }
      ),
      auth.consumeRefreshToken
    );
  }

  // Adds a person who already exists to a team, optionally starting them off
  // with some of its roles.
  static async addTeamMember(
    churchID: string,
    teamID: string,
    personID: string,
    auth: AuthContextType,
    roleIDs: string[] = []
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PUT,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/members/${personID}`,
        { roleIDs }
      ),
      auth.consumeRefreshToken
    );
  }

  static async removeTeamMember(
    churchID: string,
    teamID: string,
    personID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.DELETE,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/members/${personID}`
      ),
      auth.consumeRefreshToken
    );
  }

  // Creates the person and adds them to the team in one call.
  static async createTeamMember(
    churchID: string,
    teamID: string,
    attributes: PersonAttributes,
    auth: AuthContextType,
    roleIDs: string[] = []
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/members`,
        { attributes, roleIDs }
      ),
      auth.consumeRefreshToken
    );
  }

  // Replaces the roles a member holds. An empty list clears them.
  static async setMemberRoles(
    churchID: string,
    teamID: string,
    personID: string,
    roleIDs: string[],
    auth: AuthContextType
  ): Promise<HTTPResponse<MemberRolesResponse>> {
    return await NetworkController.performRequestAt<MemberRolesResponse>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/members/${personID}`,
        { roleIDs }
      ),
      auth.consumeRefreshToken
    );
  }

  static async createTeamRole(
    churchID: string,
    teamID: string,
    attributes: VolunteerTeamRoleAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ result: VolunteerTeamRole }>> {
    return await NetworkController.performRequestAt<{ result: VolunteerTeamRole }>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/roles`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  // Replaces every attribute, so a rename has to carry the role's training
  // rules along: the API reads a missing list as none, and would drop them.
  // Modules newly added to the rules are also assigned to the people who hold
  // the role now; the response lists what that handed out.
  static async updateTeamRole(
    churchID: string,
    teamID: string,
    roleID: string,
    attributes: VolunteerTeamRoleAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<RoleUpdateResponse>> {
    return await NetworkController.performRequestAt<RoleUpdateResponse>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.PATCH,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/roles/${roleID}`,
        { attributes }
      ),
      auth.consumeRefreshToken
    );
  }

  // Deleting a role also takes it off everyone who held it.
  static async deleteTeamRole(
    churchID: string,
    teamID: string,
    roleID: string,
    auth: AuthContextType
  ): Promise<HTTPResponse<unknown>> {
    return await NetworkController.performRequestAt<unknown>(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.DELETE,
        `/v1/church/${churchID}/volunteering/teams/${teamID}/roles/${roleID}`
      ),
      auth.consumeRefreshToken
    );
  }
}
